'use client';

/**
 * W6 · Itens para revisar — a home-card port of the Orçamento review flow
 * (`ReviewQueueCard`). It surfaces the top pendências from the same
 * `useReviewQueue` and clears them inline, with two per-row icon actions:
 *
 *   - **Confirmar** (check) keeps the suggested category as-is
 *     (`confirmCategory(categoryRef: null)`), auto-creating a merchant rule only
 *     for "você categorizou antes" items — the same default the full card uses.
 *   - **Alterar** (tag) opens the shared `CategoryPickerDrawer` to pick a
 *     different category (`confirmCategory(categoryRef: <picked>)`).
 *
 * "Confirmar todos" accepts every SUGGESTED CATEGORY in the queue (`confirmAll`), not just
 * the rows shown — and only those: a refund is resolved by linking and a duplicate needs a
 * role, so neither is confirmable here. Confirming/categorizing recategorizes transactions
 * server-side, so a
 * success sweeps the queue plus the transaction family (Top categorias / Últimas /
 * the lists) via `revalidateTransactionCaches` — mirroring the Orçamento `refresh`.
 */
import { useState, type ReactNode } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useSWRConfig } from 'swr';
import {
  ArrowsOutSimpleIcon,
  ArrowUUpLeftIcon,
  CheckIcon,
  ChecksIcon,
  CircleNotchIcon,
  CopySimpleIcon,
  ProhibitIcon,
  TagIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

import { CategoryIcon } from '@/components/CategoryIcon';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MoneyValue } from '@/components/ui/MoneyValue';
import { ApiError, categoryBudgetApi, investmentsApi, refundsApi } from '@/lib/api';
import type { TransactionRefundCandidate } from '@/lib/api';
import {
  revalidateReviewQueues,
  revalidateTransactionCaches,
  useAuthedFetcher,
} from '@/hooks/queries/swrConfig';
import { revalidateInvestmentHoldings } from '@/hooks/queries/useInvestmentHoldings';
import { revalidatePostRefundCaches } from '@/hooks/queries/useReviewQueue';
import { useReviewQueue } from '@/hooks/queries/useReviewQueue';
import { useFormatCurrency } from '@/hooks/useFormatCurrency';
import { markHomeFeedSeenIds } from '@/lib/homeFeedSeen';
import { track, trackEvent, type DuplicateDecision } from '@/lib/telemetry';
import { notifyError } from '@/lib/notify-error';
import { REASON_LABEL, resolveRefundPair } from '@/components/orcamento/helpers';
import { refundLinkToastMessage } from '@/lib/transactions/refunds';
import { categoryVisual } from '@/components/categories/categoryHelpers';
import { CategoryPickerDrawer } from '@/components/categories/CategoryPickerDrawer';
import { buildRuleAnchors, type RuleSaveOptions } from '@/components/categories/ruleAnchor';
import { retroApplyFeedback } from '@/components/categories/confirmCategoryFeedback';
import { RefundLinkPickerDrawer, type RefundPickerSubject } from '@/components/transactions/RefundLinkPickerDrawer';
import type { ReviewQueueItem } from '@/types';

import { WidgetCard, type WidgetState } from './WidgetCard';

/** The widget is a teaser, not the full queue — a few rows keep the card scannable. */
const MAX_ROWS = 4;

/**
 * Whether the one-tap Confirmar will also mint a rule. "Você categorizou antes" means the
 * user already made this call once, so confirming it teaches the rule — the same default
 * the full card uses. The row has to SAY so: a rule anchored to a merchant covers every
 * descriptor variant of it, which is far too much to create from an icon button in
 * silence.
 */
function mintsRule(item: ReviewQueueItem): boolean {
  return item.reason === 'user_categorized_before';
}

export function RevisarWidget(): ReactNode {
  const { data, error, mutate } = useReviewQueue();
  // "Confirmar todos" resolves suggested categories server-side across the WHOLE queue, not just
  // the loaded page. The category-kind total is the honest count for it — the all-kinds page is
  // capped, so counting category rows in it understates once there are more than a page of them.
  const { data: categoryQueue } = useReviewQueue('category');
  const { mutate: mutateGlobal } = useSWRConfig();
  const authedFetch = useAuthedFetcher();

  // A single row confirming (its id) and the "Confirmar todos" pass are tracked apart
  // so only the pressed control shows a busy state.
  const { userId } = useAuth();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  // Which duplicate decision is in flight, so the spinner lands on the button the user pressed.
  const [confirmingDecision, setConfirmingDecision] = useState<DuplicateDecision | null>(null);
  const [confirmingAll, setConfirmingAll] = useState(false);
  // The row whose category is being changed via the drawer (null = drawer closed).
  const [categorizing, setCategorizing] = useState<ReviewQueueItem | null>(null);
  const [categorizeSaving, setCategorizeSaving] = useState(false);
  // The refund row whose link picker is open, and its in-flight state.
  const [refundPicking, setRefundPicking] = useState<ReviewQueueItem | null>(null);
  const [refundSaving, setRefundSaving] = useState(false);
  // Confirming the whole queue is irreversible (merchants become "known", items don't
  // return) — gate it behind a dialog, like the source ReviewQueueCard.
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  const busy = confirmingId !== null || confirmingAll;

  const items = data?.results.slice(0, MAX_ROWS) ?? [];
  const total = data?.total ?? 0;
  // "Confirmar todos" accepts SUGGESTED CATEGORIES — it cannot resolve a refund, and a
  // duplicate needs a role rather than an acceptance. Counting the whole queue therefore
  // promised more than the button does; now that the queue carries three kinds it would be
  // plainly wrong. The category-kind total is exact and uncapped, so the dialog cannot promise
  // "100 lançamentos" while the action confirms 120.
  const confirmableCount = categoryQueue?.total ?? 0;

  const openConfirmAll = () => setConfirmAllOpen(true);

  const state: WidgetState = data
    ? items.length === 0
      ? 'empty'
      : 'ready'
    : error
      ? 'error'
      : 'loading';

  // Rules/confirms recategorize transactions — refresh the queue AND every transaction
  // surface (the sibling widgets read the same data), mirroring the Orçamento `refresh`.
  const refresh = () => {
    void mutate();
    // Per-kind queue keys: /budget's category slice and /transactions' refund
    // slice must follow this widget's unfiltered view.
    revalidateReviewQueues(mutateGlobal);
    revalidateTransactionCaches(mutateGlobal);
  };

  const showError = (err: unknown, fallback: string) => {
    if (err instanceof ApiError && err.isLimitExceeded()) return; // global upgrade dialog handles it
    notifyError('home_revisar', err, fallback);
  };

  // Investment duplicates resolve through their own domain endpoints, exactly as refunds do —
  // the queue is shared, the decisions are not.
  const handleDuplicateDecision = async (item: ReviewQueueItem, decision: DuplicateDecision) => {
    if (busy) return;
    const suggested = item.duplicateSuggestion?.suggestedRole === 'ADDITIVE' ? 'additive' : 'mirror';
    setConfirmingId(`${item.id}:${item.reason}`);
    setConfirmingDecision(decision);

    try {
      await authedFetch((token) => investmentsApi.decideDuplicate(token, item.id, decision));

      track('action.investment.duplicate_reviewed', {
        decision,
        suggested_role: suggested,
        followed_suggestion: decision === suggested,
        ...(item.duplicateSuggestion?.matchReason
          ? { match_reason: item.duplicateSuggestion.matchReason }
          : {}),
      });
      trackEvent('new_home.widget_action', { widget: 'revisar', action: 'confirm', count: 1 });

      refresh();
      // A grouping decision moves the portfolio total the sibling widgets read, and the holdings
      // the maturity calendar reads.
      revalidatePostRefundCaches(mutateGlobal);
      revalidateInvestmentHoldings(mutateGlobal);
    } catch (err) {
      showError(err, 'Não foi possível salvar sua decisão.');
    } finally {
      setConfirmingId(null);
      setConfirmingDecision(null);
    }
  };

  const confirmOne = (item: ReviewQueueItem) => {
    if (busy) return;
    // (id, reason) — the same transaction can appear as a refund AND a category item.
    setConfirmingId(`${item.id}:${item.reason}`);
    void (async () => {
      try {
        await authedFetch((token) =>
          categoryBudgetApi.confirmCategory(token, item.id, {
            categoryRef: null,
            // Same default as ReviewQueueRow: "sua escolha anterior" learns a rule.
            createRule: mintsRule(item),
            applyToMerchant: false,
            // The anchor the ROW discloses (see RevisarRow's "cria regra" chip), so the
            // teaser's one tap creates exactly what it showed rather than leaving the
            // grain to the server's automatic choice.
            ruleAnchor: buildRuleAnchors(item)[0].kind,
          }),
        );
        trackEvent('new_home.widget_action', { widget: 'revisar', action: 'confirm', count: 1 });
        // Deliberate double-emit: widget_action is the widget-engagement lens,
        // action.categorize.apply the cross-surface flow lens.
        track('action.categorize.apply', {
          source: 'home_revisar',
          mode: 'single',
          count: 1,
          rule_created: mintsRule(item),
        });
        // Reviewed = seen: keep the Últimas feed from flagging this row as Novo.
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        refresh();
      } catch (err) {
        showError(err, 'Não foi possível confirmar o lançamento.');
      } finally {
        setConfirmingId(null);
      }
    })();
  };

  const runConfirmAll = () => {
    if (busy || items.length === 0) return;
    setConfirmingAll(true);
    void (async () => {
      try {
        const { confirmed } = await authedFetch((token) => categoryBudgetApi.confirmAll(token));
        trackEvent('new_home.widget_action', { widget: 'revisar', action: 'confirm_all', count: confirmed });
        track('action.categorize.apply', {
          source: 'home_revisar',
          mode: 'confirm_all',
          count: confirmed,
          rule_created: false,
        });
        if (userId) markHomeFeedSeenIds(userId, (data?.results ?? []).map((queued) => queued.id));
        toast.success(
          confirmed === 1 ? '1 lançamento confirmado.' : `${confirmed} lançamentos confirmados.`,
        );
        refresh();
      } catch (err) {
        showError(err, 'Não foi possível confirmar os lançamentos.');
      } finally {
        setConfirmingAll(false);
      }
    })();
  };

  // Linking/unflagging moves money between totals — the sibling home widgets
  // and /budget's overview must follow.
  const refreshAfterRefundAction = () => {
    refresh();
    revalidatePostRefundCaches(mutateGlobal);
  };

  const confirmRefund = (item: ReviewQueueItem) => {
    const suggestion = item.refundSuggestion;
    if (busy || !suggestion) return;
    setConfirmingId(`${item.id}:${item.reason}`);
    void (async () => {
      try {
        await authedFetch((token) =>
          refundsApi.link(token, resolveRefundPair(item, suggestion.transactionId)),
        );
        track('action.refund.review_resolve', { reason: 'possible_refund', action: 'link' });
        track('action.refund.link', {
          source: 'review_queue',
          partial: Math.abs(item.amount) < Math.abs(suggestion.amount),
        });
        toast.success(refundLinkToastMessage(suggestion.amount, item.amount));
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        refreshAfterRefundAction();
      } catch (err) {
        showError(err, 'Não foi possível vincular o reembolso.');
      } finally {
        setConfirmingId(null);
      }
    })();
  };

  // "Manter como despesa": clears the expired previsto flag, removing only the
  // refund item — a category item for the same transaction stays queued.
  const keepAsExpense = (item: ReviewQueueItem) => {
    if (busy) return;
    setConfirmingId(`${item.id}:${item.reason}`);
    void (async () => {
      try {
        await authedFetch((token) => refundsApi.setExpectedRefund(token, item.id, false));
        track('action.refund.review_resolve', { reason: 'refund_not_arrived', action: 'keep_expense' });
        toast.success('Despesa mantida nos totais.');
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        refreshAfterRefundAction();
      } catch (err) {
        showError(err, 'Não foi possível confirmar o lançamento.');
      } finally {
        setConfirmingId(null);
      }
    })();
  };

  const handleRefundAction = (item: ReviewQueueItem) => {
    if (item.reason === 'refund_not_arrived') keepAsExpense(item);
    else confirmRefund(item);
  };

  const handleRefundPickerSave = async (subjectId: string, candidate: TransactionRefundCandidate) => {
    const item = refundPicking;
    if (!item || item.id !== subjectId) return;
    const notArrived = item.reason === 'refund_not_arrived';
    setRefundSaving(true);
    try {
      await authedFetch((token) =>
        refundsApi.link(token, resolveRefundPair(item, candidate.transactionId)),
      );
      track('action.refund.review_resolve', {
        reason: notArrived ? 'refund_not_arrived' : 'possible_refund',
        action: 'link',
      });
      const expenseAbs = Math.abs(notArrived ? item.amount : candidate.amount);
      const refundAbs = Math.abs(notArrived ? candidate.amount : item.amount);
      track('action.refund.link', { source: 'review_queue', partial: refundAbs < expenseAbs });
      toast.success(refundLinkToastMessage(expenseAbs, refundAbs));
      if (userId) markHomeFeedSeenIds(userId, [item.id]);
      setRefundPicking(null);
      refreshAfterRefundAction();
    } catch (err) {
      showError(err, 'Não foi possível vincular o reembolso.');
    } finally {
      setRefundSaving(false);
    }
  };

  // "Não é reembolso — manter como entrada": dismisses ONLY the refund proposal.
  // Reachable directly from the review row and from the picker's escape hatch. Tracks the
  // row's busy state via confirmingId (like confirm/keep) so a direct row dismiss disables
  // its buttons; returns whether it succeeded so the picker escape hatch closes only then.
  const handleDismissRefund = async (item: ReviewQueueItem): Promise<boolean> => {
    if (item.reason !== 'possible_refund') return false;
    setConfirmingId(`${item.id}:${item.reason}`);
    try {
      await authedFetch((token) => refundsApi.dismissRefundSuggestion(token, item.id));
      track('action.refund.review_resolve', { reason: 'possible_refund', action: 'not_refund' });
      toast.success('Mantido como entrada.');
      if (userId) markHomeFeedSeenIds(userId, [item.id]);
      refreshAfterRefundAction();
      return true;
    } catch (err) {
      showError(err, 'Não foi possível confirmar o lançamento.');
      return false;
    } finally {
      setConfirmingId(null);
    }
  };

  // Picker escape hatch: keep the picker open on failure so its context survives a retry.
  const handleNotARefund = async () => {
    const item = refundPicking;
    if (!item) return;
    setRefundSaving(true);
    try {
      if (await handleDismissRefund(item)) setRefundPicking(null);
    } finally {
      setRefundSaving(false);
    }
  };

  const handleRefundPickingOpenChange = (open: boolean) => {
    if (!open) setRefundPicking(null);
  };

  const handleCategorize = (
    item: ReviewQueueItem,
    categoryRef: string,
    options: RuleSaveOptions,
  ) => {
    setCategorizeSaving(true);
    void (async () => {
      try {
        const result = await authedFetch((token) =>
          categoryBudgetApi.confirmCategory(token, item.id, {
            categoryRef,
            createRule: options.createRule,
            applyToMerchant: options.applyToMerchant,
            ruleAnchor: options.ruleAnchor,
          }),
        );
        const feedback = retroApplyFeedback(result);
        if (feedback) toast[feedback.severity](feedback.message);
        trackEvent('new_home.widget_action', { widget: 'revisar', action: 'categorize', count: 1 });
        track('action.categorize.apply', {
          source: 'home_revisar',
          mode: 'single',
          count: 1,
          rule_created: options.createRule,
        });
        if (userId) markHomeFeedSeenIds(userId, [item.id]);
        setCategorizing(null);
        refresh();
      } catch (err) {
        showError(err, 'Não foi possível categorizar o lançamento.');
      } finally {
        setCategorizeSaving(false);
      }
    })();
  };

  const categorizeSubject = categorizing
    ? {
        id: categorizing.id,
        label: categorizing.description,
        currentCategoryId: categorizing.suggestion?.categoryRef ?? null,
        // Review-queue items have no real current category — this is only a suggestion
        // (and for 'user_categorized_before' it's labeled "Sua escolha anterior" elsewhere,
        // which would contradict an "Atual" pin/badge on the same row).
        pinCurrentCategory: false,
      }
    : null;
  const refundPickingSubject: RefundPickerSubject | null = refundPicking
    ? {
        id: refundPicking.id,
        side: refundPicking.reason === 'refund_not_arrived' ? 'expense' : 'refund',
        label: refundPicking.merchant ?? refundPicking.description,
        amount: refundPicking.amount,
        date: refundPicking.date,
      }
    : null;

  return (
    <>
      <WidgetCard
        eyebrow="Itens para revisar"
        // No "Ver todos": the queue mixes category, refund and investment items and there is no
        // page that shows all three — /budget loads only the category kind, silently dropping the
        // rest. The widget IS the all-kinds surface; "+N aguardando revisão" hints at the overflow.
        state={state}
        emptyLabel="Tudo revisado — nenhum item pendente."
        // Mobile: an empty queue hides the card entirely — it occupies a prime
        // slot right under the Panorama (design review). Desktop's masonry
        // keeps the "tudo revisado" reassurance.
        className={state === 'empty' ? 'max-md:hidden' : undefined}
      >
        {data && items.length > 0 && (
          <div className="flex h-full flex-col">
            <ul className="flex-1 divide-y divide-border-subtle" role="list">
              {items.map((item) => (
                <RevisarRow
                  key={`${item.id}:${item.reason}`}
                  item={item}
                  busy={busy}
                  confirming={confirmingId === `${item.id}:${item.reason}`}
                  confirmingDecision={confirmingId === `${item.id}:${item.reason}` ? confirmingDecision : null}
                  onConfirm={() => confirmOne(item)}
                  onAlter={() => setCategorizing(item)}
                  onConfirmRefund={() => handleRefundAction(item)}
                  onAlterRefund={() => setRefundPicking(item)}
                  onDismissRefund={() => void handleDismissRefund(item)}
                  onConfirmDuplicate={(role) => void handleDuplicateDecision(item, role === 'MIRROR' ? 'mirror' : 'additive')}
                  onDismissDuplicate={() => void handleDuplicateDecision(item, 'not_duplicate')}
                />
              ))}
            </ul>

            {total > items.length && (
              <p className="mt-2 text-2xs text-text-tertiary">
                +{total - items.length} aguardando revisão
              </p>
            )}

            {confirmableCount > 0 && (
              <footer className="mt-3 border-t border-border-subtle pt-3">
                <Button size="sm" variant="ghost" disabled={busy} onClick={openConfirmAll}>
                  <ChecksIcon className="size-4" weight="bold" aria-hidden />
                  Confirmar todos{confirmableCount > items.length ? ` (${confirmableCount})` : ''}
                </Button>
              </footer>
            )}
          </div>
        )}
      </WidgetCard>

      <CategoryPickerDrawer
        open={categorizing != null}
        onOpenChange={(open) => {
          if (!open) setCategorizing(null);
        }}
        subject={categorizeSubject}
        saving={categorizeSaving}
        onSave={(_id, categoryRef, _source, options) => handleCategorize(categorizing!, categoryRef, options)}
        ruleCreation={categorizing
          ? {
              anchors: buildRuleAnchors(categorizing),
            }
          : undefined}
      />

      <RefundLinkPickerDrawer
        open={refundPicking != null}
        onOpenChange={handleRefundPickingOpenChange}
        subject={refundPickingSubject}
        saving={refundSaving}
        onSave={handleRefundPickerSave}
        onNotARefund={refundPicking?.reason === 'possible_refund' ? handleNotARefund : undefined}
      />

      <Dialog open={confirmAllOpen} onOpenChange={setConfirmAllOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Confirmar {confirmableCount === 1 ? '1 lançamento' : `${confirmableCount} lançamentos`}?
            </DialogTitle>
            <DialogDescription>
              As categorias sugeridas ficam como estão e os estabelecimentos passam a ser
              conhecidos — estes lançamentos não voltam para a fila.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAllOpen(false)}>
              Voltar
            </Button>
            <Button
              onClick={() => {
                setConfirmAllOpen(false);
                runConfirmAll();
              }}
            >
              <ChecksIcon className="size-4" weight="bold" aria-hidden />
              Confirmar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function RevisarRow({
  item,
  busy,
  confirming,
  confirmingDecision,
  onConfirm,
  onAlter,
  onConfirmRefund,
  onAlterRefund,
  onDismissRefund,
  onConfirmDuplicate,
  onDismissDuplicate,
}: {
  item: ReviewQueueItem;
  busy: boolean;
  confirming: boolean;
  confirmingDecision: DuplicateDecision | null;
  onConfirm: () => void;
  onAlter: () => void;
  onConfirmRefund: () => void;
  onAlterRefund: () => void;
  onDismissRefund: () => void;
  onConfirmDuplicate: (role: 'MIRROR' | 'ADDITIVE') => void;
  onDismissDuplicate: () => void;
}): ReactNode {
  const formatCurrency = useFormatCurrency();
  const dateLabel = new Date(item.date).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
  // Refund review items resolve by linking, not by confirming a category — the
  // teaser offers no wrong-tool shortcut; "Ver todas" leads to the full queue.
  const refundRow = item.reason === 'possible_refund' || item.reason === 'refund_not_arrived';
  // A duplicate decides a grouping, so it offers neither a category nor a link.
  const duplicateRow = item.reason === 'duplicate_investment';
  // Refund and duplicate items carry their own payload instead of a category proposal.
  const suggestion = !refundRow && !duplicateRow && item.suggestion ? item.suggestion : null;
  const visual = suggestion ? categoryVisual(suggestion.categoryRef, suggestion.source) : null;

  // The icon alone does not say what is being reviewed — a duplicated holding and an
  // uncategorised purchase are different decisions with different consequences, so each row
  // names its kind outright.
  const kindLabel = duplicateRow ? 'Investimento' : 'Transação';

  // Only where Confirmar actually mints one — a category row that does not learn a rule
  // has nothing to disclose, and the teaser stays as terse as it is today.
  const ruleAnchor = !refundRow && !duplicateRow && mintsRule(item) ? buildRuleAnchors(item)[0] : null;

  const handleConfirmMirror = () => onConfirmDuplicate('MIRROR');
  const handleConfirmAdditive = () => onConfirmDuplicate('ADDITIVE');
  const handleDismissDuplicate = () => onDismissDuplicate();

  // The suggested role is the emphasised action; the other stays outline. Hard-coding mirror as
  // the primary button pushed the wrong decision for an additive suggestion.
  const suggestsAdditive = item.duplicateSuggestion?.suggestedRole === 'ADDITIVE';

  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 py-2 first:pt-0 last:pb-0">
      {suggestion && visual ? (
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-xl"
          style={{ backgroundColor: visual.backgroundColor }}
          aria-hidden
        >
          <CategoryIcon name={suggestion.icon} className="size-4" style={{ color: visual.color }} />
        </span>
      ) : (
        <span
          className="flex size-8 shrink-0 items-center justify-center rounded-xl bg-surface-sunken"
          aria-hidden
        >
          {duplicateRow ? (
            <CopySimpleIcon className="size-4 text-text-tertiary" weight="duotone" />
          ) : (
            <ArrowUUpLeftIcon className="size-4 text-text-tertiary" weight="duotone" />
          )}
        </span>
      )}
      <div className="min-w-0 flex-1">
        <span className="mb-0.5 inline-block rounded-full border border-border-subtle px-1.5 py-px text-3xs font-semibold text-text-secondary">
          {kindLabel}
        </span>
        <p className="truncate text-sm font-medium text-text-primary">{item.description}</p>
        <p className="truncate text-xs text-text-tertiary">
          {suggestion ? `${suggestion.name} · ` : ''}
          {duplicateRow && item.merchant ? `${item.merchant} · ` : ''}
          {REASON_LABEL[item.reason]} · {dateLabel}
        </p>
        {/* One tap here creates a rule. On its own line rather than appended to the
            metadata above, which truncates first on a phone — the disclosure would be the
            part that disappeared. It names the anchor because the two cover very
            different sets. */}
        {ruleAnchor && (
          <p className="truncate text-2xs text-text-tertiary">
            Confirmar cria regra para{' '}
            <b className="font-medium text-text-secondary">
              {ruleAnchor.kind === 'merchant' ? `tudo de ${ruleAnchor.label}` : ruleAnchor.label}
            </b>
          </p>
        )}
      </div>
      <span className="shrink-0 font-mono text-sm text-text-primary tabular-nums">
        <MoneyValue text={formatCurrency(item.amount)} />
      </span>
      {duplicateRow ? (
        <div className="flex w-full shrink-0 items-center justify-end gap-1 md:w-auto">
          <Button
            size="icon-sm"
            variant="ghost"
            disabled={busy}
            title="Não é duplicado"
            aria-label={`Não é duplicado — manter ${item.description} separado`}
            onClick={handleDismissDuplicate}
          >
            {confirmingDecision === 'not_duplicate' ? (
              <CircleNotchIcon className="size-4 animate-spin" aria-hidden />
            ) : (
              <ProhibitIcon className="size-4" aria-hidden />
            )}
          </Button>
          {/* Two acceptances, not one: "mesma aplicação" counts the money once, "aplicações
              diferentes" sums it. The teaser offers both because picking the wrong one is
              what silently loses or doubles a balance. */}
          <Button
            size="icon-sm"
            variant={suggestsAdditive ? 'default' : 'outline'}
            disabled={busy}
            title="Aplicações diferentes"
            aria-label={`${item.description}: aplicações diferentes`}
            onClick={handleConfirmAdditive}
          >
            {confirmingDecision === 'additive' ? (
              <CircleNotchIcon className="size-4 animate-spin" aria-hidden />
            ) : (
              <ArrowsOutSimpleIcon className="size-4" aria-hidden />
            )}
          </Button>
          <Button
            size="icon-sm"
            variant={suggestsAdditive ? 'outline' : 'default'}
            disabled={busy}
            title="Mesma aplicação"
            aria-label={`${item.description}: mesma aplicação`}
            onClick={handleConfirmMirror}
          >
            {confirmingDecision === 'mirror' ? (
              <CircleNotchIcon className="size-4 animate-spin" aria-hidden />
            ) : (
              <CheckIcon className="size-4" weight="bold" aria-hidden />
            )}
          </Button>
        </div>
      ) : refundRow ? (
        <div className="flex w-full shrink-0 items-center justify-end gap-1 md:w-auto">
          {/* "Não é reembolso" — reject the suggestion so the inflow stays income.
              Only a possible_refund can be dismissed; refund_not_arrived keeps its
              "Manter como despesa" flow. */}
          {item.reason === 'possible_refund' && (
            <Button
              size="icon-sm"
              variant="ghost"
              disabled={busy}
              title="Não é reembolso"
              aria-label={`Não é reembolso — manter ${item.description} como entrada`}
              onClick={onDismissRefund}
            >
              <ProhibitIcon className="size-4" aria-hidden />
            </Button>
          )}
          {/* ArrowUUpLeft (not the category TagIcon): the two decision kinds
              must read differently at a glance. */}
          <Button
            size="icon-sm"
            variant="outline"
            disabled={busy}
            title={item.reason === 'refund_not_arrived' ? 'Vincular estorno' : 'Alterar vínculo'}
            aria-label={item.reason === 'refund_not_arrived'
              ? `Vincular estorno de ${item.description}`
              : `Alterar vínculo de ${item.description}`}
            onClick={onAlterRefund}
          >
            <ArrowUUpLeftIcon className="size-4" aria-hidden />
          </Button>
          <Button
            size="icon-sm"
            disabled={busy || (item.reason === 'possible_refund' && !item.refundSuggestion)}
            title={item.reason === 'refund_not_arrived' ? 'Manter como despesa' : 'Confirmar'}
            aria-label={item.reason === 'refund_not_arrived'
              ? `Manter ${item.description} como despesa`
              : `Confirmar ${item.description}`}
            onClick={onConfirmRefund}
          >
            {confirming ? (
              <CircleNotchIcon className="size-4 animate-spin" aria-hidden />
            ) : (
              <CheckIcon className="size-4" weight="bold" aria-hidden />
            )}
          </Button>
        </div>
      ) : (
        <div className="flex w-full shrink-0 items-center justify-end gap-1 md:w-auto">
          <Button
            size="icon-sm"
            variant="outline"
            disabled={busy}
            title="Alterar categoria"
            aria-label={`Alterar categoria de ${item.description}`}
            onClick={onAlter}
          >
            <TagIcon className="size-4" aria-hidden />
          </Button>
          <Button
            size="icon-sm"
            disabled={busy}
            title="Confirmar"
            aria-label={`Confirmar ${item.description}`}
            onClick={onConfirm}
          >
            {confirming ? (
              <CircleNotchIcon className="size-4 animate-spin" aria-hidden />
            ) : (
              <CheckIcon className="size-4" weight="bold" aria-hidden />
            )}
          </Button>
        </div>
      )}
    </li>
  );
}
